"use client";

import type { BabyNameEntry } from "@/lib/api/baby-names";
import Badge from "@ishvaram/ui/Badge";
import { ScoreBadge } from "@ishvaram/ui/ScoreBadge";

interface NameCardProps {
  entry: BabyNameEntry;
  selected: boolean;
  onToggle: () => void;
}

export function NameCard({ entry, selected, onToggle }: NameCardProps) {
  const genderVariant =
    entry.gender === "male" ? "accent" : entry.gender === "female" ? "error" : "accent";

  return (
    <button
      type="button"
      aria-pressed={selected}
      aria-label={`${selected ? "Remove" : "Add"} ${entry.name} ${
        selected ? "from" : "to"
      } shortlist`}
      className={`card-sacred w-full cursor-pointer border-2 p-4 text-left transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-intent-brand focus-visible:ring-offset-2 ${
        selected ? "border-border-interactive bg-surface-brand-subtle" : "border-transparent"
      }`}
      onClick={onToggle}
      data-design-owner="baby-name-card"
    >
      <span className="flex items-start justify-between gap-3">
        <span className="min-w-0 flex-1">
          <span className="mb-1 flex items-center gap-2">
            <span
              role="heading"
              aria-level={3}
              className="truncate font-serif text-body font-bold text-text-primary"
            >
              {entry.name}
            </span>
            <Badge label={entry.gender} variant={genderVariant} className="capitalize" />
          </span>
          <span className="mb-1 block text-body-small text-text-secondary">{entry.name_hi}</span>
          <span className="block text-body-small leading-snug text-text-secondary">
            {entry.meaning}
          </span>
          <span className="mt-2 flex items-center gap-3 text-caption font-medium text-text-muted">
            <span>Letter: {entry.starting_letter}</span>
            <span>Name No: {entry.name_number}</span>
            <span>{entry.origin}</span>
          </span>
        </span>
        <span className="flex flex-shrink-0 flex-col items-center">
          <ScoreBadge score={entry.compatibility_score} max={10} size="lg" />
          <span className="mt-1 text-caption font-bold uppercase tracking-eyebrow text-text-muted">
            {entry.compatibility_score >= 9
              ? "Excellent"
              : entry.compatibility_score >= 7
                ? "Good"
                : "Fair"}
          </span>
        </span>
      </span>
      {selected && (
        <span className="mt-2 block rounded bg-surface-brand-subtle py-1 text-center text-caption font-bold text-intent-brand">
          Added to shortlist
        </span>
      )}
    </button>
  );
}
